export type EventOutcomePriority = 'p1' | 'p2' | 'p3' | 'p4' | 'p5';

export type EventOutcomePriorityFilter = 'all' | EventOutcomePriority;

export interface EventOutcomeItem {
  id: string;
  label: string;
  priority: EventOutcomePriority;
}

export interface EventOutcomeGroup {
  priority: EventOutcomePriority;
  items: EventOutcomeItem[];
}

export interface EventOutcomeConfirm {
  outcomeId: string;
  closingDetails?: string;
}

export const EVENT_OUTCOME_PRIORITY_ORDER: EventOutcomePriority[] = [
  'p1',
  'p2',
  'p3',
  'p4',
  'p5',
];

export const EVENT_OUTCOME_GROUP_COLORS: Record<EventOutcomePriority, string> = {
  p1: 'var(--eventq-color-blue-950)',
  p2: 'var(--eventq-color-red-950)',
  p3: 'var(--eventq-color-orange-950)',
  p4: 'var(--eventq-color-green-950)',
  p5: 'var(--eventq-color-purple-950)',
};

export const EVENT_OUTCOME_GROUP_BORDER_COLORS: Record<EventOutcomePriority, string> = {
  p1: 'var(--eventq-color-blue-900)',
  p2: 'var(--eventq-color-red-900)',
  p3: 'var(--eventq-color-orange-900)',
  p4: 'var(--eventq-color-green-900)',
  p5: 'var(--eventq-color-purple-900)',
};

export const EVENT_OUTCOME_PRIORITY_ICONS: Record<EventOutcomePriority, string> = {
  p1: '/assets/events/icons/priority-p1.svg',
  p2: '/assets/events/icons/priority-p2.svg',
  p3: '/assets/events/icons/priority-p3.svg',
  p4: '/assets/events/icons/priority-p4.svg',
  p5: '/assets/events/icons/priority-p5.svg',
};

export const EVENT_OUTCOME_PRIORITY_FILTER_COLORS: Record<EventOutcomePriority, string> = {
  p1: 'var(--eventq-color-blue-950)',
  p2: 'var(--eventq-color-red-950)',
  p3: 'var(--eventq-color-orange-950)',
  p4: 'var(--eventq-color-green-950)',
  p5: 'var(--eventq-color-purple-950)',
};

/**
 * When `emptyMeansAll` is true (Agent Raise/Close default), `[]` means every priority.
 * When false (Resolve Queue), `[]` means none selected.
 */
export function isAllOutcomePrioritiesSelected(
  selectedPriorities: readonly EventOutcomePriority[],
  availablePriorities: readonly EventOutcomePriority[],
  emptyMeansAll = true,
): boolean {
  if (availablePriorities.length === 0) {
    return true;
  }

  if (selectedPriorities.length === 0) {
    return emptyMeansAll;
  }

  return selectedPriorities.length >= availablePriorities.length;
}

export function isOutcomePrioritySelected(
  priority: EventOutcomePriority,
  selectedPriorities: readonly EventOutcomePriority[],
  availablePriorities: readonly EventOutcomePriority[],
  emptyMeansAll = true,
): boolean {
  if (isAllOutcomePrioritiesSelected(selectedPriorities, availablePriorities, emptyMeansAll)) {
    return false;
  }

  return selectedPriorities.includes(priority);
}

export function toggleOutcomePrioritySelection(
  priority: EventOutcomePriority | 'all',
  selectedPriorities: readonly EventOutcomePriority[],
  availablePriorities: readonly EventOutcomePriority[],
  emptyMeansAll = true,
): EventOutcomePriority[] {
  if (priority === 'all') {
    if (emptyMeansAll) {
      return [];
    }

    return isAllOutcomePrioritiesSelected(selectedPriorities, availablePriorities, false)
      ? []
      : [...availablePriorities];
  }

  if (isAllOutcomePrioritiesSelected(selectedPriorities, availablePriorities, emptyMeansAll)) {
    return emptyMeansAll
      ? availablePriorities.filter((item) => item !== priority)
      : [priority];
  }

  if (selectedPriorities.includes(priority)) {
    if (selectedPriorities.length === 1 && !emptyMeansAll) {
      return [...availablePriorities];
    }
    return selectedPriorities.filter((item) => item !== priority);
  }

  const next = [...selectedPriorities, priority];
  if (next.length >= availablePriorities.length) {
    return emptyMeansAll ? [] : [...availablePriorities];
  }

  return next;
}

export function matchesOutcomePrioritySelection(
  priority: EventOutcomePriority,
  selectedPriorities: readonly EventOutcomePriority[],
  availablePriorities: readonly EventOutcomePriority[],
  emptyMeansAll = true,
): boolean {
  if (isAllOutcomePrioritiesSelected(selectedPriorities, availablePriorities, emptyMeansAll)) {
    return true;
  }

  if (selectedPriorities.length === 0) {
    return false;
  }

  return selectedPriorities.includes(priority);
}
